import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { Button, LoadingModal, Stack, Text } from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { FormContainer, InputPassword, PasswordInputValidator } from 'Components/utility';
import { ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import { useAccount, useApiError, usePasswordValidation } from 'Hooks';
import { resetPass } from 'Services/auth';
import { type RootState } from 'src/reducers';
import i18n from 'Utilities/i18n';
import { Container } from './helpers/';
import { ResetFailure, ResetSuccess } from './states';

const mapStateToProps = ({
  config: { validation },
  nav: {
    query: { email, token, product, version },
  },
  auth: {
    include: { patient },
  },
}: RootState) => ({
  validation,
  email,
  token,
  product,
  version,
  patient,
});

type MyFormValues = {
  newPassword: string;
  repeatNewPassword: string;
};

const initialValuesFormik: MyFormValues = {
  newPassword: '',
  repeatNewPassword: '',
};

const connector = connect(mapStateToProps);

type ResetPasswordFlowState = 'form' | 'success' | 'failure';

type Props = ConnectedProps<typeof connector>;

const ResetPassword = ({ patient, validation, token, email, product, version }: Props) => {
  const validationSchema = usePasswordValidation({
    validateCurrentPassword: false,
    hasNewPassword: true,
    patient,
    translations: {
      newPassword: {
        required: i18n.t(
          'ResetPassword.content.resetPasswordForm.formField.newPassword.errors.required'
        ),
        minLength: () => '',
        // PasswordInputValidator handles this error message
        // if needed, uncomment the following lines to return the error message
        // minLength: ({ count }) =>
        //   i18n.t('ResetPassword.content.resetPasswordForm.formField.newPassword.errors.min', {
        //     count,
        //   }),
        maxLength: ({ count }) =>
          i18n.t('ResetPassword.content.resetPasswordForm.formField.newPassword.errors.max', {
            count,
          }),
        trim: i18n.t('ResetPassword.content.resetPasswordForm.formField.newPassword.errors.trim'),
        repetitiveSequence: ({ count }) =>
          i18n.t(
            'ResetPassword.content.resetPasswordForm.formField.newPassword.errors.repetitiveSequence',
            { count }
          ),
        context: i18n.t(
          'ResetPassword.content.resetPasswordForm.formField.newPassword.errors.context'
        ),
        dictionary: i18n.t(
          'ResetPassword.content.resetPasswordForm.formField.newPassword.errors.dictionary'
        ),
        mustMatch: i18n.t(
          'ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.errors.passwordMustMatch'
        ),
        repeatRequired: i18n.t(
          'ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.errors.required'
        ),
        differentFromCurrent: i18n.t(
          'ResetPassword.content.resetPasswordForm.formField.newPassword.errors.differentFromCurrent'
        ),
      },
    },
  });

  const [flowState, setFlowState] = useState<ResetPasswordFlowState>('form');
  const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);

  const { showApiErrorModal, showNetworkErrorModal } = useApiError();
  const { showPasswordValidationError } = useAccount();

  const onSubmitForm = async ({ newPassword }: MyFormValues) => {
    try {
      await resetPass(newPassword, token, product, version);
      setFlowState('success');
    } catch (error) {
      handleApiRequestError(error as AxiosError<ApiErrorData>);
    }
  };

  const {
    handleSubmit,
    handleBlur,
    setValues,
    handleChange,
    isValid,
    errors,
    touched,
    values,
    setTouched,
    isSubmitting,
  } = useFormik({
    initialValues: initialValuesFormik,
    onSubmit: onSubmitForm,
    validationSchema,
    validateOnBlur: true,
  });

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>) => {
      switch (error.status) {
        case ApiStatus.BAD_REQUEST: {
          const errors = error.response?.data.errors;

          setValues(initialValuesFormik);
          setTouched({});

          showPasswordValidationError({ validation }, errors);
          break;
        }

        case ApiStatus.UNAUTHORIZED: {
          setFlowState('failure');
          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      setTouched,
      showApiErrorModal,
      showNetworkErrorModal,
      showPasswordValidationError,
      setValues,
      validation,
    ]
  );

  if (flowState === 'success') {
    return (
      <Container>
        <ResetSuccess />
      </Container>
    );
  }

  if (flowState === 'failure') {
    return (
      <Container>
        <ResetFailure />
      </Container>
    );
  }

  const isFormSubmittable =
    values.newPassword && values.repeatNewPassword && isValid && meetsPasswordRequirements;

  // for the email to show up please add `&email=someemail@email.com` to the url
  return (
    <>
      {isSubmitting && <LoadingModal aria-label="Loading" />}
      <Container>
        <Stack width="100%" justifyContent="center" alignItems="center" gap="$2">
          <Text type="$h2.strong" testID="ResetPassword.title">
            {i18n.t('ResetPassword.title')}
          </Text>
          <Text textAlign="center" testID="ResetPassword.subtitle">{`${i18n.t(
            'ResetPassword.subtitle'
          )}\n${email}`}</Text>
        </Stack>

        <FormContainer hasHiddenUserInput>
          <PasswordInputValidator
            placeholder={i18n.t(
              'ResetPassword.content.resetPasswordForm.formField.newPassword.placeholder'
            )}
            label={i18n.t('ResetPassword.content.resetPasswordForm.formField.newPassword.label')}
            onChangeText={handleChange('newPassword')}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur('newPassword')}
            password={values.newPassword}
            onValidate={setMeetsPasswordRequirements}
            identities={validation?.identity}
            testID="newPassword"
          />
          <InputPassword
            label={i18n.t(
              'ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.label'
            )}
            placeholder={i18n.t(
              'ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.placeholder'
            )}
            testID="repeatNewPassword"
            onBlur={handleBlur('repeatNewPassword')}
            onChangeText={handleChange('repeatNewPassword')}
            onSubmitEditing={() => (isFormSubmittable ? handleSubmit() : {})}
            defaultValue={values.repeatNewPassword}
            isInvalid={Boolean(errors.repeatNewPassword && touched.repeatNewPassword)}
            errorMessage={errors.repeatNewPassword}
          />
        </FormContainer>

        <Button
          testID="ResetPassword.button"
          onPress={() => {
            handleSubmit();
          }}
          disabled={!isFormSubmittable}
          label={i18n.t('ResetPassword.title')}
          aria-label={i18n.t('ResetPassword.title')}
        />
      </Container>
    </>
  );
};

export default connector(ResetPassword);
